import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import { DateCell } from '../../../../components/table/DateCell';
import { TextCell } from '../../../../components/table/TextCell';
import { formatMoney } from '../../../../lib/formatMoney';
import { stockMovementsApi } from '../stockApi';
import type { StockMovementListItem } from '../stockTypes';
import { movementTypeLabel } from '../stockTypes';

/**
 * The stock ledger — every change to stock, one line each.
 *
 * **Read-only by construction**, and every affordance the shell would otherwise offer is simply
 * not configured. There is no Create, because a movement is written by `StockLedger` as part of
 * the batch or unit operation that moved the stock — a line written on its own would leave the
 * on-hand figure and the ledger free to disagree, which is the one failure this table exists to
 * prevent. There is no Edit and no Delete, because `AppendOnlyInterceptor` throws on either.
 * There is no Clear data, because wiping a ledger is the destruction of the record.
 *
 * **No Show deleted and no folder-nav strip**, and neither is an omission: the shell derives
 * both from whether the page declares a `status` facet, and an append-only table has nothing
 * deleted to file by. The audit trail is the standing precedent for both.
 *
 * **THERE IS NO PROFILE PAGE, and the anchor opens the ITEM instead.** That is a stated gap
 * rather than a decision the index↔profile rule already excuses — a ledger line is a stored
 * record, not a container and not a derived view, so it does not fall under either exemption.
 * What made it acceptable to ship without one is that the row's whole content is on screen:
 * a movement carries nine fields and every one of them has a column here or sits in the Other
 * Info popover. The anchor carries `inventory-items.read` — the key of what it OPENS, never
 * this page's own — which is the same call Stock Levels' anchor takes for the same reason.
 */
export function StockMovementsIndexPage() {
  const navigate = useNavigate();

  /*
    The anchor opens the ITEM whose stock moved. See the remarks above: a ledger line has no
    page of its own, and the item is the record a reader who clicks a line is actually after.

    It lands on the item's PROFILE rather than on a filtered ledger, which is the same shortfall
    Stock Levels' "Stock detail" already has by the same navigation — neither index reads a
    filter from the URL. Filtering this list by item is the one change that fixes both.
  */
  const openItem = useCallback(
    (row: StockMovementListItem) => navigate(`/inventory/settings/items/${row.inventoryItemId}`),
    [navigate],
  );

  const fetchMovements = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      stockMovementsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,

          // Every facet the rail offers is sent. One the server does not read renders values
          // and counts, then filters nothing when clicked — a control that lies.
          item: query.facets.item?.join(','),
          facility: query.facets.facility?.join(','),
          movementType: query.facets.movementType?.join(','),
          direction: query.facets.direction?.join(','),
        },
        signal,
      ),
    [],
  );

  return (
    <IndexPage<StockMovementListItem>
      module="stock-movements"
      /*
        The one opt-out, and this table earns it the way the audit trail does: its own
        `Recorded` column IS the creation instant and its `Recorded by` IS the creator, so
        letting the shell append the trio would print each of them twice.

        It drops the list view's provenance lead in the same flag, which is the half that is
        easy to miss — without it the list rows would still carry the actor and the instant
        under every title while the table beside them already had columns for both.
      */
      auditColumns={false}
      breadcrumb={['Logistics', 'Inventory', 'Stock Movements']}
      columns={COLUMNS}
      /*
        Coarse to fine, and DIRECTION is its own axis rather than more values on the type: an
        Adjustment is signed either way, so "what kind of movement" and "did stock arrive or
        leave" are two questions one list of checkboxes could not answer.

        NO `status` group, and its absence is what tells the shell there is nothing deleted
        here — see the page's own remarks.
      */
      facets={[
        { key: 'movementType', label: 'Movement' },
        { key: 'direction', label: 'Direction' },
        { key: 'item', label: 'Item' },

        // The facility a reader filters by is the one the movement names, which the row
        // already carries — so no `storage-facilities.read` gate: a gate here could never hide
        // anything, which is the defect the personnel list's plantilla columns are named for.
        { key: 'facility', label: 'Facility' },
      ]}
      auditEntityTypes={['StockMovement']}
      rowKey={(row) => row.id}
      fetch={fetchMovements}
      searchPlaceholder="Search item, SKU, lot, serial or reason"
      emptyMessage="Nothing has moved yet."
      onRowOpen={openItem}
      openPermission="inventory-items.read"
      rowActions={(row) => [
        {
          key: `open-item-${row.id}`,
          label: 'Open item',
          permission: 'inventory-items.read',
          onClick: () => openItem(row),
        },
      ]}
      /*
        The row's own provenance, which for an append-only line is the creation and nothing
        else. The update pair is NULL rather than mirrored onto the creation: a movement has
        never been updated, and stating otherwise would invent an event.
      */
      otherInfo={(row) => ({
        createdAt: row.occurredAt,
        createdByName: row.actorName,
        updatedAt: null,
        updatedByName: null,
      })}
    />
  );
}

/**
 * Columns map to `StockMovementListItem`.
 *
 * The ITEM leads, because that is what a reader comes here holding — "where did the gloves go"
 * — and it is the row's click target. The instant is beside it, where a list already sorted by
 * recency reads as a sequence rather than as the thing being looked up: the same order the
 * audit trail settles on.
 */
const COLUMNS: ColumnDef<StockMovementListItem>[] = [
  {
    /*
      The key IS the server's sort field — `itemName`, which is what the allow-list names. A
      column keyed `item` would send a field the server does not know, fall back to the default
      sort, and read as a broken control.
    */
    key: 'itemName',
    header: 'Item',
    width: 240,
    flex: true,
    sortable: true,

    /*
      ONE value, and the SKU takes its own sortable column beside it — the call the item
      catalog's own list already makes for the same pair. A stacked anchor is refused inside
      `<IndexTable>` and the merged two-value anchor is the panel table's exception, so neither
      is available here and neither is needed: the two are separately useful to sort by.
    */
    render: (row) => <TextCell value={row.itemName} />,
  },
  {
    key: 'occurredAt',
    header: 'Recorded',
    width: 180,
    sortable: true,
    summary: true,
    render: (row) => <DateCell value={row.occurredAt} />,
  },
  {
    key: 'sku',
    header: 'SKU',
    width: 150,
    sortable: true,
    render: (row) => <TextCell value={row.sku} mono />,
  },
  {
    key: 'movementType',
    header: 'Movement',
    width: 130,
    sortable: true,
    summary: true,
    // The DIRECTION rides in the label for an Adjustment — Positive or Negative, read off the
    // sign of the quantity two columns along. See `movementTypeLabel`.
    render: (row) => <TextCell value={movementTypeLabel(row.movementType, row.quantity)} />,
  },
  {
    /*
      SIGNED, and rendered with its sign. That is the whole ledger: an item's balance at a
      facility is the sum of its movements and nothing else, so a magnitude beside a direction
      word would be the same fact split across two columns and harder to scan than the number.

      `numeric` mutes a zero — which cannot occur, because the ledger declines to append a line
      for a delta of nothing. It is passed anyway, because the column holds a quantity and that
      is what the prop declares; it is not the cell sniffing its own value.
    */
    key: 'quantity',
    header: 'Quantity',
    width: 110,
    align: 'right',
    sortable: true,
    summary: true,
    render: (row) => (
      <TextCell value={`${row.quantity > 0 ? '+' : ''}${row.quantity}`} mono numeric />
    ),
  },
  {
    key: 'unitCode',
    header: 'Unit',
    width: 80,
    render: (row) => <TextCell value={row.unitCode} mono />,
  },
  {
    /*
      What a unit was worth when it MOVED — a snapshot the ledger wrote, and never re-read.

      It has a column because it has a value: a wire field nothing renders is the
      collected-stored-and-never-read defect the item's own `ExpiryThresholdDays` is the
      standing example of, and it is easier to commit on a DTO than on a column because it
      costs no migration.

      An em dash where the moving row carried no price, never a zero — a lot nobody costed is a
      different fact from one that was free, which is the same distinction every other money
      column in the suite draws.
    */
    key: 'unitCostSnapshot',
    header: 'Value at move',
    width: 140,
    align: 'right',
    render: (row) => (
      <TextCell
        value={row.unitCostSnapshot === null ? null : formatMoney(row.unitCostSnapshot)}
        mono
        numeric
      />
    ),
  },
  {
    key: 'storageFacilityName',
    header: 'Facility',
    width: 180,
    sortable: true,
    render: (row) => <TextCell value={row.storageFacilityName} />,
  },
  {
    /*
      The lot OR the serial, in one column, because exactly one of them is ever set: a movement
      is a quantity movement or a serialized one. Two columns would be two that are an em dash
      on every row between them, which is the width a row's own facts need.

      One line, one header, one width — the merged-anchor rule read one column along, and the
      cost is the same: neither half can be sorted on its own. Neither was a useful order.
    */
    key: 'lot',
    header: 'Lot / Serial',
    width: 170,
    render: (row) => <TextCell value={row.batchNumber ?? row.serialNumber} mono />,
  },
  {
    key: 'reason',
    header: 'Reason',
    width: 240,
    render: (row) => <TextCell value={row.reason} />,
  },
  {
    key: 'actorName',
    header: 'Recorded by',
    width: 180,
    render: (row) => <TextCell value={row.actorName} size="xs" />,
  },
];
